import styles from "./ForgotPassword.module.scss";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Form } from "antd";
import { getLanguageFromLocal } from "../../../helper/language";
import { AuthService } from "../../../services/auth.service";
import { IForgotPasswordModel } from "../../../models/User";
import { passwordValidation } from "../../../helper/auth";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import FormItemInputPassword from "../../../components/ui/FormItemInputPassword/FormItemInputPassword";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openErrorMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<1 | 2>(1);
  const [token, setToken] = useState<string>("");

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get("otp-token");
      searchParams.delete("otp-token");
      if (token) {
        navigate("/auth/forgot-password");
        setToken(token);
        setStep(2);
      }
    }
  }, [location]);

  const onFinishStep1 = async (values: { email: string }) => {
    const model = {
      email: values.email.trim(),
      lang: getLanguageFromLocal(),
    };

    try {
      const response = await AuthService.sendForgotPasswordMail(model);

      if (response.status === 200 && response.data) {
        navigate(
          `/success?title=${t("CheckYourEmail")}&description=${t("DescriptionCheckYourEmail", { email: model.email })}`
        );
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const onFinishStep2 = async (values: IForgotPasswordModel) => {
    const model = {
      newPassword: values.newPassword.trim(),
      confirmNewPassword: values.confirmNewPassword.trim(),
    };

    if (!passwordValidation(model.newPassword)) {
      dispatch(openErrorMessage(t("ValidationPassword")));
      return;
    }

    if (model.newPassword !== model.confirmNewPassword) {
      dispatch(openErrorMessage(t("ValidationPassword")));
      return;
    }

    try {
      const response = await AuthService.forgotPassword(token, model);
      if (response.status === 200 && response.data) {
        navigate(
          `/success?title=${t("PasswordSuccessfullyChanged")}&description=${t("YourPasswordForClubPassUpdatedSuccessfully")}&linkPath=/auth/login&linkText=${t("GoToLogin")}`
        );
      }
    } catch (e) {
      handleError(e, dispatch, t);
      setStep(1);
    }
  };

  return (
    <>
      <div className={styles.formWrapper}>
        <div className={styles.box}>
          {step === 1 && (
            <>
              <h2 className={styles.title}>{t("ResetYourPassword")}</h2>
              <p className={styles.text}>{t("EnterEmailAddressUsedToCreateAccount")}</p>
              <Form
                className={styles.form}
                name="email"
                initialValues={{ remember: true }}
                onFinish={onFinishStep1}
                autoComplete="off"
                layout={"vertical"}
              >
                <FormItemInput
                  name={"email"}
                  label={t("EmailAddress")}
                  rules={{ required: true }}
                />

                <div className={styles.btnWrapper}>
                  <CustomButton text={t("Submit")} type={"submit"} typeStyle={"fill"} />
                </div>
              </Form>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className={styles.title}>{t("ResetYourPassword")}</h2>
              <p className={styles.text}>{t("EnterNewPasswordForYourAccount")}</p>
              <Form
                className={styles.form}
                name="forgot-password"
                initialValues={{ remember: true }}
                onFinish={onFinishStep2}
                autoComplete="off"
                layout={"vertical"}
              >
                <FormItemInputPassword
                  name={"newPassword"}
                  label={t("NewPassword")}
                  rules={{ required: true }}
                />

                <FormItemInputPassword
                  name={"confirmNewPassword"}
                  label={t("ConfirmNewPassword")}
                  rules={{ required: true }}
                />

                <div className={styles.btnWrapper}>
                  <CustomButton text={t("Submit")} type={"submit"} typeStyle={"fill"} />
                </div>
              </Form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
